import React, { forwardRef, useImperativeHandle, useState, useEffect, useMemo } from 'react';
import { Box, Flex, Button, NumberInput, NumberInputField, useToast, Input, Switch, Text, FormControl } from '@chakra-ui/core';
import { StringArrayInput } from '../../../../app/components/StringArrayInput';
import isEqual from 'lodash/isEqual';
import { Column } from 'react-table';
import { NUM_INPUT_FIELD_STYLES, TEXT_INPUT_FIELD_STYLES } from '../constants';
import { ParameterItem, PaneHandle } from '../accountParameter.types';
import { DataTable } from './DataTable';
import { isValidUrl, isValidEmail } from '../utils';
import { useTranslation } from 'react-i18next';
import { useSettingsDisabled } from '../SettingsDisabledContext';

export interface GenericSettingsPaneHandle extends PaneHandle {
  resetDirtyState: () => void;
}

interface GenericSettingsPaneProps {
  items: ParameterItem[];
  onSave?: (updates: Record<string, any>) => Promise<boolean>;
  setDirty: (isDirty: boolean) => void;
  onRowsChange?: (rows: ParameterItem[]) => void;
  showInlineActions?: boolean;
  externalResetCounter?: number;
}

export const GenericSettingsPane = forwardRef<GenericSettingsPaneHandle, GenericSettingsPaneProps>(
  ({ items, onSave, setDirty, onRowsChange, showInlineActions = true, externalResetCounter }, ref) => {
    const toast = useToast();
    const { t } = useTranslation('accountParameter');
    const disabled = useSettingsDisabled();
    const [initialItems, setInitialItems] = useState<ParameterItem[]>(() => JSON.parse(JSON.stringify(items)));
    const [rows, setRows] = useState<ParameterItem[]>(() => JSON.parse(JSON.stringify(items)));
    const [isDirty, setIsDirty] = useState(false);
    const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});

    const itemsString = useMemo(() => JSON.stringify(items), [items]);

    useEffect(() => {
      if (!isDirty) {
        const newItems = JSON.parse(itemsString);
        setInitialItems(newItems);
        setRows(newItems);
      }
    }, [itemsString, isDirty]);

    useEffect(() => {
      const dirty = !isEqual(initialItems, rows);
      setIsDirty(dirty);
      setDirty(dirty);
    }, [rows, initialItems, setDirty]);

    useEffect(() => {
      onRowsChange?.(rows);
    }, [rows, onRowsChange]);

    useEffect(() => {
      if (typeof externalResetCounter === 'number' && externalResetCounter > 0) {
        setRows(initialItems);
      }
    }, [externalResetCounter, initialItems]);

    const handleSave = async () => {
      const updates: Record<string, any> = {};
      const newErrors: Record<string, string | undefined> = {};
      let hasValidationErrors = false;

      rows.forEach((row) => {
        if (row.type === 'number') {
          if (typeof row.min === 'number' && (row.value as number) < row.min) {
            newErrors[row.key] = t('genericSettingsPane.valueBelowRecommendedMinimum', { min: row.min });
            hasValidationErrors = true;
          } else if (typeof row.max === 'number' && (row.value as number) > row.max) {
            newErrors[row.key] = t('genericSettingsPane.valueAboveRecommendedMaximum', { max: row.max });
            hasValidationErrors = true;
          }
        } else if (row.type === 'url') {
          if (!isValidUrl(row.value as string)) {
            newErrors[row.key] = t('genericSettingsPane.invalidUrl');
            hasValidationErrors = true;
          }
        } else if (row.type === 'email') {
          if (!isValidEmail(row.value as string)) {
            newErrors[row.key] = t('genericSettingsPane.invalidEmailAddress');
            hasValidationErrors = true;
          }
        }

        const initialItem = initialItems.find((item) => item.key === row.key);
        if (initialItem && !isEqual(row.value, initialItem.value)) {
          updates[row.key] = row.value;
        }
      });

      setValidationErrors(newErrors);

      if (hasValidationErrors) {
        toast({ status: 'error', title: t('genericSettingsPane.pleaseFixValidationErrorsBeforeSaving'), isClosable: true });
        return false;
      }

      if (Object.keys(updates).length > 0) {
        if (!onSave) {
          return false;
        }
        const success = await onSave(updates);
        if (success) {
          setInitialItems(JSON.parse(JSON.stringify(rows)));
        }
        return success;
      } else {
        toast({ status: 'info', title: t('genericSettingsPane.noChangesToSave'), isClosable: true });
        return true; // No changes, so consider it a success
      }
    };

    const handleReset = () => {
      setInitialItems(JSON.parse(JSON.stringify(items))); // Re-initialize from the original items prop
      setRows(JSON.parse(JSON.stringify(items))); // Re-initialize from the original items prop
      setIsDirty(false);
      setDirty(false);
      setValidationErrors({}); // Clear validation errors on reset
    };

    useImperativeHandle(ref, () => ({
      save: async () => {
        if (!onSave) {
          return Promise.resolve(false);
        }
        const success = await handleSave();
        return success;
      },
      reset: handleReset,
      resetDirtyState: () => {
        setInitialItems(JSON.parse(JSON.stringify(items)));
      },
    }));

    const columns = useMemo<Column<ParameterItem>[]>(
      () => [
        {
          Header: t('genericSettingsPane.parameter') as string,
          accessor: 'label',
          Cell: ({ value }: { value: string }) => <Text fontWeight={600}>{value}</Text>,
        },
        {
          Header: t('genericSettingsPane.description') as string,
          accessor: 'description',
          disableSortBy: true,
          Cell: ({ value }: { value: string }) => <Text fontSize="xs">{value}</Text>,
        },
        {
          Header: t('genericSettingsPane.value') as string,
          id: 'value',
          disableSortBy: true,
          Cell: ({ row }: { row: { original: ParameterItem } }) => {
            const item = row.original;

            if (item.type === 'boolean') {
              return (
                <FormControl>
                  <Switch
                    isChecked={item.value as boolean}
                    isDisabled={disabled}
                    onChange={(e) => {
                      const nextVal = e.target.checked;
                      setRows((prev) => prev.map((p) => (p.key === item.key ? { ...p, value: nextVal } : p)));
                    }}
                  />
                  {validationErrors[item.key] && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {validationErrors[item.key]}
                    </Text>
                  )}
                </FormControl>
              );
            } else if (item.type === 'string' || item.type === 'url' || item.type === 'email') {
              // Handle url and email here
              return (
                <>
                  <Input
                    value={item.value as string}
                    isDisabled={disabled}
                    onChange={(e) => {
                      setRows((prev) => prev.map((p) => (p.key === item.key ? { ...p, value: e.target.value } : p)));
                    }}
                    {...TEXT_INPUT_FIELD_STYLES}
                    maxW="360px"
                  />
                  {validationErrors[item.key] && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {validationErrors[item.key]}
                    </Text>
                  )}
                </>
              );
            } else if (item.type === 'string[]') {
              return (
                <>
                  <StringArrayInput
                    value={item.value as string[]}
                    itemKey={item.key}
                    onValueChange={(key, newValue) => {
                      setRows((prev) => prev.map((p) => (p.key === key ? { ...p, value: newValue } : p)));
                    }}
                  />
                  {validationErrors[item.key] && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {validationErrors[item.key]}
                    </Text>
                  )}
                </>
              );
            } else if (item.type === 'number') {
              return (
                <Flex align="center" direction="column" alignItems="flex-start">
                  {' '}
                  {/* Adjust Flex for error message */}
                  <Flex align="center">
                    <NumberInput
                      size="sm"
                      maxW="88px"
                      value={item.value as number}
                      min={item.min}
                      max={item.max}
                      isDisabled={disabled}
                      onChange={(_, valueAsNumber) => {
                        setRows((prev) => prev.map((p) => (p.key === item.key ? { ...p, value: valueAsNumber || 0 } : p)));
                      }}
                    >
                      <NumberInputField {...NUM_INPUT_FIELD_STYLES} />
                    </NumberInput>
                    {item.unit && (
                      <Text ml="10px" fontSize="sm">
                        {item.unit}
                      </Text>
                    )}
                  </Flex>
                  {validationErrors[item.key] && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {validationErrors[item.key]}
                    </Text>
                  )}
                </Flex>
              );
            }
            // Default case for unhandled types, though with proper ParameterItem definition, this shouldn't be reached for expected types.
            return null;
          },
        },
      ],
      [validationErrors, t, disabled], // Dependency on validationErrors and t to re-render cells on error change or translation change
    );

    return (
      <Box>
        <DataTable
          columns={columns}
          data={rows}
          getRowProps={(row: { original: ParameterItem }) => ({
            className: !isEqual(row.original.value, initialItems.find((item) => item.key === row.original.key)?.value)
              ? 'dirty'
              : '',
          })}
        />
        {showInlineActions && isDirty && (
          <Flex justify="flex-end" mt={4}>
            <Button variant="ghost" mr={3} onClick={handleReset}>
              {t('genericSettingsPane.reset')}
            </Button>
            <Button colorScheme="blue" onClick={handleSave} isDisabled={!isDirty || Object.keys(validationErrors).length > 0}>
              {t('genericSettingsPane.save')}
            </Button>
          </Flex>
        )}
      </Box>
    );
  },
);
